import { useRef, useState } from 'react';
import { useFormContext, useFieldArray, type FieldPath } from 'react-hook-form';
import { ImagePlus, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { InvitationFormValues } from '@/types/invitation-schema';
import { VESTIMENTA_CATALOGO, ITINERARIO_CATALOGO } from '@/invitation/payload/system-catalog';

interface Props {
  invitationId: string | undefined;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

type Path = FieldPath<InvitationFormValues>;

const DetailsTab = ({ invitationId }: Props) => {
  const form = useFormContext<InvitationFormValues>();
  const { user } = useAuth();
  const [uploading, setUploading] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const itinerary = useFieldArray({ control: form.control, name: 'sections.itinerario' });
  const retratos = form.watch('sections.retratos') ?? 'pareja';

  const uploadTo = async (path: Path, slug: string, file: File) => {
    if (!invitationId || !user) return;
    if (!ALLOWED_TYPES.includes(file.type)) { toast.error('Solo imágenes JPG, PNG, WebP o HEIC'); return; }
    if (file.size > MAX_FILE_SIZE) { toast.error('La imagen no debe superar 5 MB'); return; }
    setUploading(slug);
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const filePath = `${user.id}/${invitationId}/${slug}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('invitation-photos').upload(filePath, file, { upsert: true });
    setUploading(null);
    if (error) { toast.error('Error al subir la foto'); return; }
    const { data } = supabase.storage.from('invitation-photos').getPublicUrl(filePath);
    form.setValue(path, data.publicUrl as never, { shouldDirty: true });
  };

  if (!invitationId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Guarda la invitación primero para editar fotos y detalles.
        </CardContent>
      </Card>
    );
  }

  const PhotoSlot = ({ path, slug, label, hint }: { path: Path; slug: string; label: string; hint: string }) => {
    const url = form.watch(path) as string | undefined;
    return (
      <div className="space-y-2">
        <Label className="text-sm">{label}</Label>
        <div
          className="relative aspect-[3/4] rounded-lg border border-dashed border-muted-foreground/30 overflow-hidden bg-muted/40 flex items-center justify-center cursor-pointer"
          onClick={() => inputRefs.current[slug]?.click()}
        >
          {url ? (
            <>
              <img src={url} alt={label} className="w-full h-full object-cover" />
              <Button type="button" variant="destructive" size="icon" className="absolute top-1 right-1 h-7 w-7"
                onClick={(e) => { e.stopPropagation(); form.setValue(path, undefined as never, { shouldDirty: true }); }}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <div className="text-center text-muted-foreground p-2">
              <ImagePlus className="w-6 h-6 mx-auto mb-1" />
              <span className="text-xs">{uploading === slug ? 'Subiendo…' : 'Subir'}</span>
            </div>
          )}
          <input ref={(el) => { inputRefs.current[slug] = el; }} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTo(path, slug, f); e.target.value = ''; }} />
        </div>
        <p className="text-[11px] text-muted-foreground leading-tight">{hint}</p>
      </div>
    );
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Fotos principales</CardTitle>
          <CardDescription>Portada y retratos. La galería “Nosotros” va en la pestaña Galería.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <PhotoSlot path="sections.fotos.portada" slug="portada" label="Portada" hint="Fondo de la portada" />
            <PhotoSlot path="sections.fotos.pareja" slug="pareja" label="Retrato de pareja" hint="Vertical 2:3, sale en arco" />
            <PhotoSlot path="sections.fotos.novia" slug="novia" label="Retrato de la novia" hint="Solo en retratos individuales" />
            <PhotoSlot path="sections.fotos.novio" slug="novio" label="Retrato del novio" hint="Solo en retratos individuales" />
          </div>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Fotos de los lugares</CardTitle>
          <CardDescription>Una foto opcional por sede; se muestran en la sección de eventos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium">Mostrar fotos de los lugares</span>
            <Switch
              checked={form.watch('sections.fotosLugares') ?? false}
              onCheckedChange={(v) => form.setValue('sections.fotosLugares', v, { shouldDirty: true })}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <PhotoSlot path="sections.fotos.sedes.religiosa" slug="sede-relig" label="Ceremonia religiosa" hint="Foto de la sede" />
            <PhotoSlot path="sections.fotos.sedes.civil" slug="sede-civil" label="Ceremonia civil" hint="Foto de la sede" />
            <PhotoSlot path="sections.fotos.sedes.recepcion" slug="sede-recep" label="Recepción" hint="Foto de la sede" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Historia y retratos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Modo de retratos</Label>
            <Select value={retratos} onValueChange={(v) => form.setValue('sections.retratos', v as 'pareja' | 'individuales' | 'sin', { shouldDirty: true })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pareja">En pareja</SelectItem>
                <SelectItem value="individuales">Individuales</SelectItem>
                <SelectItem value="sin">Sin retratos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Frase de la pareja (opcional)</Label>
            <Input placeholder="Doce años, tres ciudades y un perro después…" {...form.register('sections.historiaFrase')} />
          </div>
          {retratos === 'individuales' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nombre completo (novia)</Label>
                <Input placeholder="Ana Salas Medina" {...form.register('sections.noviaNombreCompleto')} />
              </div>
              <div className="space-y-2">
                <Label>Nombre completo (novio)</Label>
                <Input placeholder="Diego Cordero Ríos" {...form.register('sections.novioNombreCompleto')} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vestimenta, álbum y RSVP</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Código de vestimenta</Label>
            <Select value={form.watch('sections.vestimenta.codigo') ?? ''} onValueChange={(v) => form.setValue('sections.vestimenta.codigo', v, { shouldDirty: true })}>
              <SelectTrigger><SelectValue placeholder="Elige un código" /></SelectTrigger>
              <SelectContent>
                {VESTIMENTA_CATALOGO.map((v) => <SelectItem key={v.codigo} value={v.codigo}>{v.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium">Foto junto a la vestimenta</span>
            <Switch checked={form.watch('sections.fotoVestimenta') ?? false} onCheckedChange={(v) => form.setValue('sections.fotoVestimenta', v, { shouldDirty: true })} />
          </div>
          <div className="max-w-[160px]"><PhotoSlot path="sections.fotos.vestimenta" slug="vestimenta" label="Foto de vestimenta" hint="Inspiración de vestimenta" /></div>
          <div className="space-y-2">
            <Label>Hashtag del álbum</Label>
            <Input placeholder="#AnaYDiego2026" {...form.register('sections.album.hashtag')} />
            <p className="text-xs text-muted-foreground">
              El álbum de fotos se configura en la pestaña Galería (subida y moderación).
            </p>
          </div>
          <div className="space-y-2">
            <Label>Fecha límite de confirmación</Label>
            <p className="text-xs text-muted-foreground">
              Se define en «Mi boda» (fecha límite para confirmar) y se muestra automáticamente en
              la invitación. Así el cierre del RSVP y la fecha que ven tus invitados son siempre la misma.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Itinerario</CardTitle>
          <CardDescription>Elige cada momento de un catálogo (cada uno con su ícono).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {itinerary.fields.map((f, idx) => (
            <div key={f.id} className="flex flex-wrap items-end gap-3 p-3 border rounded-lg">
              <div className="space-y-1 min-w-[180px] flex-1">
                <Label className="text-xs">Momento</Label>
                <Select
                  value={(form.watch(`sections.itinerario.${idx}.momento`) as string) || ITINERARIO_CATALOGO[0].id}
                  onValueChange={(v) => {
                    form.setValue(`sections.itinerario.${idx}.momento`, v, { shouldDirty: true });
                    if (v !== 'otro') form.setValue(`sections.itinerario.${idx}.momentoLibre`, '', { shouldDirty: true });
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ITINERARIO_CATALOGO.map((m) => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}
                    <SelectItem value="otro">Otro…</SelectItem>
                  </SelectContent>
                </Select>
                {form.watch(`sections.itinerario.${idx}.momento`) === 'otro' && (
                  <Input
                    className="mt-1"
                    placeholder="Escribe el momento"
                    {...form.register(`sections.itinerario.${idx}.momentoLibre`)}
                  />
                )}
              </div>
              <div className="space-y-1 w-28">
                <Label className="text-xs">Hora</Label>
                <Input placeholder="18:30" {...form.register(`sections.itinerario.${idx}.hora`)} />
              </div>
              <div className="space-y-1 min-w-[160px] flex-1">
                <Label className="text-xs">Detalle (opcional)</Label>
                <Input placeholder="Menú de tres tiempos" {...form.register(`sections.itinerario.${idx}.detalle`)} />
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => itinerary.remove(idx)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => itinerary.append({ momento: ITINERARIO_CATALOGO[0].id, hora: '', detalle: '' })}>
            <Plus className="w-4 h-4 mr-1" /> Añadir momento
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default DetailsTab;
